/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import approveTrip from '../utils/approveTrip';
import { format } from 'date-fns';
import { toET, todayET } from '../utils/et';

const DRIVER_COLORS = ['#E1F5EE', '#E6F1FB', '#EEEDFE', '#FAEEDA', '#FAECE7'];
const DRIVER_TEXT = ['#085041', '#0C447C', '#3C3489', '#854F0B', '#993C1D'];
const HOME_BASE = '552 W King St, Lancaster, PA';

function Badge({ bg, color, label }) {
  return (
    <span style={{ background: bg, color, fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function DriverPill({ driver, index, onRemove }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      background: DRIVER_COLORS[index % DRIVER_COLORS.length],
      color: DRIVER_TEXT[index % DRIVER_TEXT.length],
      borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 500,
    }}>
      {driver.name}
      <span onClick={onRemove} style={{ cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</span>
    </div>
  );
}

// Check if a vehicle is busy at a given time based on existing transports
function isVehicleBusy(vehicleId, scheduledTime, existingTransports, excludeId) {
  if (!scheduledTime) return false;
  const newStart = new Date(scheduledTime).getTime();
  const now = Date.now();
  return existingTransports.some(t => {
    if (t.id === excludeId) return false;
    if (t.vehicle_id !== vehicleId) return false;
    if (['cancelled', 'completed', 'pending_approval'].includes(t.status)) return false;
    const tStart = new Date(t.scheduled_time).getTime();
    const tEnd = t.estimated_return_time
      ? new Date(t.estimated_return_time).getTime()
      : tStart + (t.estimated_duration_minutes || 60) * 60000;
    // If estimated return has passed, vehicle is free regardless of status
    if (tEnd < now) return false;
    return newStart >= tStart && newStart < tEnd;
  });
}

// Check if a driver is busy at a given time
function isDriverBusy(driverId, scheduledTime, existingTransports, excludeId) {
  if (!scheduledTime) return false;
  const newStart = new Date(scheduledTime).getTime();
  const now = Date.now();
  return existingTransports.some(t => {
    if (t.id === excludeId) return false;
    if (['cancelled', 'completed', 'pending_approval'].includes(t.status)) return false;
    const assignees = t.assignees || [];
    if (!assignees.find(a => (a.driverId || a.driver_id) === driverId)) return false;
    const tStart = new Date(t.scheduled_time).getTime();
    const tEnd = t.estimated_return_time
      ? new Date(t.estimated_return_time).getTime()
      : tStart + (t.estimated_duration_minutes || 60) * 60000;
    // If estimated return has passed, driver is free
    if (tEnd < now) return false;
    return newStart >= tStart && newStart < tEnd;
  });
}

// Driver -> vehicle stickiness maps. Completed trips on the trip's own (ET)
// date still count: once a driver is put in a vehicle for the day they stay
// suggested there after finishing their runs, instead of falling back to the
// least-busy vehicle (Paula 2026-08-25). Sorted so the latest trip wins --
// a deliberate mid-day vehicle switch carries forward.
function buildDriverVehicleMaps(existingTransports, transport) {
  const targetDate = transport?.scheduled_time
    ? format(toET(transport.scheduled_time), 'yyyy-MM-dd')
    : todayET();
  const driverToVehicle = {};
  const vehicleToDrivers = {};
  existingTransports
    .filter(t => {
      if (!t.vehicle_id) return false;
      if (['cancelled', 'pending_approval'].includes(t.status)) return false;
      if (t.status === 'completed') {
        return t.scheduled_time && format(toET(t.scheduled_time), 'yyyy-MM-dd') === targetDate;
      }
      return true;
    })
    .sort((a, b) => new Date(a.scheduled_time || 0) - new Date(b.scheduled_time || 0))
    .forEach(t => {
      (t.assignees || []).forEach(a => {
        const did = a.driverId || a.driver_id;
        if (did) {
          driverToVehicle[did] = t.vehicle_id;
          if (!vehicleToDrivers[t.vehicle_id]) vehicleToDrivers[t.vehicle_id] = new Set();
          vehicleToDrivers[t.vehicle_id].add(did);
        }
      });
    });
  return { driverToVehicle, vehicleToDrivers };
}

export function getSuggestions(transport, vehicles, drivers, existingTransports = [], priorities = {}) {
  const isBH = ['201', '302', '303', '304'].includes(transport.bh_code || transport.patient_type);
  const needsWC = transport.requires_wheelchair;
  const isPrivate = !(transport.request_raw_text || '').toLowerCase().match(/lgh|lbhh|lrh|willow/);
  const isLGH = (transport.request_raw_text || '').toLowerCase().includes('lgh') ||
                (transport.origin_address || '').toLowerCase().includes('duke st');
  const scheduledTime = transport.scheduled_time;

  // Determine required driver count
  let driverCount = 1;
  if (!needsWC) driverCount = 2;
  else if (isPrivate) driverCount = 2;
  else if (isLGH) driverCount = 1;
  else driverCount = 1;

  // Filter available vehicles
  const availableVehicles = vehicles.filter(v => {
    if (v.status !== 'active') return false;
    if (needsWC && v.vehicle_type !== 'wheelchair_van') return false;
    if (scheduledTime && isVehicleBusy(v.id, scheduledTime, existingTransports, transport.id)) return false;
    return true;
  });

  // Filter available qualified drivers
  const availableDrivers = drivers.filter(d => {
    if (!d.isAvailable) return false;
    if (d.capabilities?.attendantOnly) return false;
    if (needsWC && !d.capabilities?.wheelchair) return false;
    if (isBH && !d.capabilities?.bh201) return false;
    const bhCode = transport.bh_code || transport.patient_type;
    if (['302', '303', '304'].includes(bhCode) && (d.capabilities?.avoidAggressive302 || d.capabilities?.bhCalmOnly)) return false;
    if (['302', '303', '304'].includes(bhCode) && !d.capabilities?.bh302) return false;
    if (scheduledTime && isDriverBusy(d.id, scheduledTime, existingTransports, transport.id)) return false;
    return true;
  });

  // Which driver is in which vehicle today (completed trips count -- see helper)
  const { driverToVehicle, vehicleToDrivers } = buildDriverVehicleMaps(existingTransports, transport);

  // Fairness scoring: prioritize drivers who need hours most.
  // Score = how far below their weekly target they are (higher = needs more hours = pick first).
  // Full-time drivers get priority over part-time when both need hours.
  const scoredDrivers = availableDrivers.map(d => {
    const hoursThisWeek = d.weeklyStats?.hoursThisWeek || 0;
    const targetMin = d.targetHoursMin || (d.isFullTime ? 35 : 15);
    const targetMax = d.targetHoursMax || (d.isFullTime ? 45 : 25);
    const hoursRemaining = Math.max(0, targetMin - hoursThisWeek);
    const pctOfTarget = targetMin > 0 ? hoursThisWeek / targetMin : 1;

    // Higher score = should get the next trip
    let score = 0;
    score += hoursRemaining * 10;          // More hours needed → higher score
    score += (1 - pctOfTarget) * 50;       // Further from target → higher score
    score += d.isFullTime ? 20 : 0;        // FT gets slight priority
    score -= (d.hos?.todayMinutes || 0) / 60; // Already worked a lot today → lower score
    // Prefer drivers already assigned to a vehicle today (crew consistency)
    if (driverToVehicle[d.id]) score += 15;
    // Dispatch priority bonus — #1 gets +100, #2 gets +90, etc.
    if (priorities[d.id]) score += Math.max(0, 110 - priorities[d.id] * 10);

    return { ...d, fairnessScore: score };
  }).sort((a, b) => b.fairnessScore - a.fairnessScore);

  // Select drivers, keeping existing crew pairs together.
  // If we need 2 drivers: pick the top-scored driver first, then strongly prefer their current partner.
  let suggestedDrivers = [];
  if (driverCount >= 2 && scoredDrivers.length >= 2) {
    const first = scoredDrivers[0];
    suggestedDrivers = [first];
    // Find their current partner (same vehicle today)
    const firstVehicleId = driverToVehicle[first.id];
    if (firstVehicleId && vehicleToDrivers[firstVehicleId]) {
      const partnerIds = [...vehicleToDrivers[firstVehicleId]].filter(id => id !== first.id);
      const partner = scoredDrivers.find(d => partnerIds.includes(d.id));
      if (partner) {
        suggestedDrivers.push(partner);
      }
    }
    // If no partner found from vehicle, pick the next highest scorer
    if (suggestedDrivers.length < driverCount) {
      for (const d of scoredDrivers) {
        if (suggestedDrivers.find(s => s.id === d.id)) continue;
        suggestedDrivers.push(d);
        if (suggestedDrivers.length >= driverCount) break;
      }
    }
  } else {
    suggestedDrivers = scoredDrivers.slice(0, driverCount);
  }

  // Pick the vehicle: prefer the vehicle the suggested drivers are already using today.
  // This keeps crews in the same vehicle instead of shuffling every trip.
  let suggestedVehicle = null;
  for (const d of suggestedDrivers) {
    const currentVehicleId = driverToVehicle[d.id];
    if (currentVehicleId) {
      const match = availableVehicles.find(v => v.id === currentVehicleId);
      if (match) { suggestedVehicle = match; break; }
    }
  }
  // Fallback: pick the least-busy available vehicle
  if (!suggestedVehicle) {
    const vehicleTripsCount = {};
    existingTransports.filter(t => !['cancelled','completed','pending_approval'].includes(t.status)).forEach(t => {
      if (t.vehicle_id) vehicleTripsCount[t.vehicle_id] = (vehicleTripsCount[t.vehicle_id] || 0) + 1;
    });
    availableVehicles.sort((a, b) => (vehicleTripsCount[a.id] || 0) - (vehicleTripsCount[b.id] || 0));
    suggestedVehicle = availableVehicles[0] || null;
  }

  // Determine if we can fulfill this trip
  let canFulfill = true;
  let reason = null;

  if (!suggestedVehicle) {
    canFulfill = false;
    const allWC = vehicles.filter(v => v.vehicle_type === 'wheelchair_van' && v.status === 'active');
    const allSedan = vehicles.filter(v => v.vehicle_type === 'sedan' && v.status === 'active');
    if (needsWC && allWC.length === 0) reason = 'No wheelchair vans in fleet';
    else if (needsWC) reason = 'All wheelchair vans are currently busy at this time';
    else if (allSedan.length === 0) reason = 'No sedans in fleet';
    else reason = 'All vehicles are currently busy at this time';
  } else if (availableDrivers.length === 0) {
    canFulfill = false;
    if (isBH) reason = 'No BH-capable drivers available at this time';
    else reason = 'No available drivers at this time';
  } else if (availableDrivers.length < driverCount) {
    canFulfill = false;
    reason = `Need ${driverCount} drivers but only ${availableDrivers.length} available at this time`;
  }

  return {
    suggestedVehicle,
    suggestedDrivers,
    availableVehicles,
    availableDrivers,
    canFulfill,
    reason,
    driverCount,
  };
}

export default function AssignmentPanel({ transport, onClose, onDispatched, existingTransports = [] }) {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasConflict, setHasConflict] = useState(false);
  const [driverVehicleMap, setDriverVehicleMap] = useState({});
  const [dispatchPriorities, setDispatchPriorities] = useState({});
  const [canFulfill, setCanFulfill] = useState(true);
  const [fulfillReason, setFulfillReason] = useState(null);

  const tripDate = transport.scheduled_time
    ? format(toET(transport.scheduled_time), 'yyyy-MM-dd')
    : todayET();

  useEffect(() => {
    Promise.all([
      api.get('/vehicles'),
      api.get(`/drivers/available?date=${tripDate}${transport.scheduled_time ? '&time=' + new Date(transport.scheduled_time).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' }) : ''}`),
    ]).then(([vRes, dRes]) => {
      const activeVehicles = vRes.data.vehicles.filter(v => v.status === 'active');
      const allDrivers = [...dRes.data.available, ...dRes.data.unavailable];
      setVehicles(activeVehicles);
      setDrivers(allDrivers);

      // Driver→vehicle map for the add-a-driver auto-switch (completed trips count)
      setDriverVehicleMap(buildDriverVehicleMaps(existingTransports, transport).driverToVehicle);

      // Fetch dispatch priorities for today
      api.get(`/drivers/dispatch-priorities?date=${tripDate}`)
        .then(priRes => {
          const priMap = {};
          (priRes.data.priorities || []).forEach(p => { priMap[p.driver_id] = p.priority_order; });
          setDispatchPriorities(priMap);
        }).catch(() => {});

      // If trip already has an assignment (any status beyond pending), pre-fill with current
      const hasAssignment = transport.vehicle_id || (transport.assignees && transport.assignees.length > 0);
      if (hasAssignment) {
        const currentVehicle = activeVehicles.find(v => v.id === transport.vehicle_id);
        const currentDrivers = (transport.assignees || [])
          .map(a => allDrivers.find(d => d.id === (a.driverId || a.driver_id)))
          .filter(Boolean);
        setSelectedVehicle(currentVehicle || null);
        setSelectedDrivers(currentDrivers.length > 0 ? currentDrivers : []);
        setCanFulfill(true);
      } else {
        const suggestions = getSuggestions(transport, activeVehicles, allDrivers, existingTransports, dispatchPriorities);
        setSelectedVehicle(suggestions.suggestedVehicle);
        setSelectedDrivers(suggestions.suggestedDrivers);
        setCanFulfill(suggestions.canFulfill);
        setFulfillReason(suggestions.reason);
      }
    }).catch(() => {
      setError('Could not load vehicles and drivers');
    }).finally(() => setLoading(false));
  }, []);

  const toggleDriver = (driver) => {
    setSelectedDrivers(prev => {
      const exists = prev.find(d => d.id === driver.id);
      if (exists) return prev.filter(d => d.id !== driver.id);
      // When adding a driver, auto-switch to their current vehicle if they have one
      const theirVehicleId = driverVehicleMap[driver.id];
      if (theirVehicleId) {
        const theirVehicle = vehicles.find(v => v.id === theirVehicleId);
        // Only follow the driver into a vehicle the TRIP can use. Bobby was in
        // the Black CRV for a later sedan run; tapping him on a wheelchair trip
        // silently selected the CRV (not even shown in the WC-only list) and
        // lit the "1 transporter on a sedan" warning (Paula 2026-10-06, 4115).
        const fits = theirVehicle && (!transport.requires_wheelchair || theirVehicle.vehicle_type === 'wheelchair_van');
        if (fits) setSelectedVehicle(theirVehicle);
      }
      return [...prev, driver];
    });
  };

  // Warning for facilities that should only have 1 transporter.
  // 2026-08-17 (Paula): LGH-billed trips are EXEMPT — LGH pays per
  // transporter regardless of drop-off, so warning on every LGH -> LRH/WV
  // discharge trained dispatchers to ignore the alert, and the one trip
  // where it mattered (TRP-2026-2988, genuinely LRH-billed) got ignored.
  // The 1-transporter rule is about who PAYS: warn only when the trip
  // originates at LRH/WV (their request = their bill).
  const singleTransporterFacilities = ['LRH', 'Lancaster Rehab', '675 Good Dr', 'Willow Valley', 'Willow Street'];
  const lghBilledOrigin = ['555 n duke', '2160 state rd'].some(a =>
    (transport.origin_address || '').toLowerCase().includes(a));
  const isSingleTransporterTrip = !lghBilledOrigin && singleTransporterFacilities.some(f =>
    (transport.origin_address || '').toLowerCase().includes(f.toLowerCase())
  );
  const showTransporterWarning = isSingleTransporterTrip && selectedDrivers.length > 1;
  // Patient-profile assist flag (Wright/3135, 2026-08-21): warn the OTHER
  // direction too — flagged patient with fewer than 2 drivers selected.
  const needsTwoAssist = ((transport.notes || '') + (transport.special_instructions || '')).toLowerCase().includes('2-person assist');
  const showTwoAssistWarning = needsTwoAssist && selectedDrivers.length === 1;

  const handleDispatch = async (forceAssign = false) => {
    if (!selectedVehicle) { setError('Please select a vehicle'); return; }
    if (selectedDrivers.length === 0) { setError('Please assign at least one driver'); return; }

    // Acuity ruling (Paula 2026-07-03): 1 transporter on a BH sedan is a
    // deliberate, confirmed, logged choice — never a silent default.
    const dispatchIsBH = ['201', '302', '303', '304', '305', 'bh'].includes(transport.bh_code || transport.patient_type);
    const dispatchIsSedan = String(selectedVehicle.vehicle_type || '').toLowerCase().includes('sedan');
    let bhConfirm = null;
    if (dispatchIsBH && dispatchIsSedan && selectedDrivers.length === 1) {
      const code = transport.bh_code || transport.patient_type;
      const involuntary = ['302', '303', '304'].includes(code);
      const behavioralNotes = transport.special_instructions
        || ((transport.notes || '').match(/Note:\s*([^|]+)/) || [])[1]
        || 'none on this trip';
      const confirmed = window.confirm(
        `ONE transporter on a BH ${code} ${involuntary ? '(INVOLUNTARY)' : '(voluntary)'} sedan trip.\n\n` +
        `Behavioral notes: ${behavioralNotes}\n` +
        `Transporter: ${selectedDrivers[0].name || selectedDrivers[0].first_name || 'selected driver'}\n\n` +
        `This is your call — confirm you have weighed the patient against the transporter. ` +
        `Your confirmation will be logged.`
      );
      if (!confirmed) return;
      const me = JSON.parse(localStorage.getItem('nemt_user') || '{}');
      bhConfirm = {
        singleTransporterBhConfirmed: true,
        confirmedBy: `${me.firstName || ''} ${me.lastName || ''}`.trim() || me.email || 'dispatcher',
      };
    }

    setSaving(true);
    setError('');
    try {
      // Only approve if still in pending state — skip for dispatched/in-progress trips
      const inProgressStatuses = ['dispatched', 'en_route_pickup', 'picked_up', 'dropped_off'];
      if (!['approved', ...inProgressStatuses].includes(transport.status)) {
        if (!(await approveTrip(transport.id))) { setSaving(false); return; }
      }
      await api.post(`/transports/${transport.id}/assign`, {
        vehicleId: selectedVehicle.id,
        forceAssign,
        splitLegsConfirmed: forceAssign,
        ...(bhConfirm || {}),
        assignments: selectedDrivers.map((d, i) => ({
          driverId: d.id,
          role: i === 0 ? 'driver_1' : 'driver_2',
          hourlyRate: parseFloat(d.hourlyRate || 40),
        })),
      });
      onDispatched();
      onClose();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.requiresBhConfirm) {
        setError('This BH sedan trip has 1 transporter selected — the confirm prompt is required. Re-click dispatch and confirm.');
      } else if (err.response?.status === 409) {
        setError('⚠️ Conflict: ' + err.response.data.message + ' — click "Dispatch anyway" to override.');
        setHasConflict(true);
      } else {
        setError('Could not dispatch — ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setSaving(false);
    }
  };

  const isBH = ['201', '302', '303', '304', '305', 'bh'].includes(transport.bh_code || transport.patient_type);
  const tripTime = transport.scheduled_time ? format(toET(transport.scheduled_time), 'h:mm a') : '--';

  // Figure out which vehicles are busy for display
  const busyVehicleIds = new Set(
    existingTransports
      .filter(t => t.id !== transport.id && !['cancelled','completed','pending_approval'].includes(t.status))
      .filter(t => {
        if (!transport.scheduled_time || !t.scheduled_time) return false;
        const newStart = new Date(transport.scheduled_time);
        const tEnd = t.estimated_return_time
          ? new Date(t.estimated_return_time)
          : new Date(new Date(t.scheduled_time).getTime() + (t.estimated_duration_minutes || 90) * 60000);
        return newStart < tEnd;
      })
      .map(t => t.vehicle_id)
      .filter(Boolean)
  );

  const busyDriverIds = new Set(
    existingTransports
      .filter(t => t.id !== transport.id && !['cancelled','completed','pending_approval'].includes(t.status))
      .filter(t => {
        if (!transport.scheduled_time || !t.scheduled_time) return false;
        const newStart = new Date(transport.scheduled_time);
        const tEnd = t.estimated_return_time
          ? new Date(t.estimated_return_time)
          : new Date(new Date(t.scheduled_time).getTime() + (t.estimated_duration_minutes || 90) * 60000);
        return newStart < tEnd;
      })
      .flatMap(t => (t.assignees || []).map(a => a.driverId))
      .filter(Boolean)
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={onClose} style={{ flex: 1, background: 'rgba(0,0,0,0.3)' }} />
      <div style={{ width: '100%', maxWidth: 440, background: 'white', display: 'flex', flexDirection: 'column', overflowY: 'auto', position: 'relative', zIndex: 1001 }}>

        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e0e0db', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>Assign & dispatch</h2>
            <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{transport.transport_number}</div>
          </div>
          <button onClick={onClose} style={{ fontSize: 13, padding: '4px 10px' }}>Cancel</button>
        </div>

        <div style={{ padding: '14px 20px', background: '#f9f9f8', borderBottom: '0.5px solid #e0e0db' }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
            {tripTime} — {transport.destination_address || 'Destination TBD'}
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
            From: {transport.origin_address || 'Pickup TBD'}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {transport.requires_wheelchair && <Badge bg="#EEEDFE" color="#3C3489" label="Wheelchair" />}
            {isBH && <Badge bg="#FAECE7" color="#993C1D" label={`BH ${transport.bh_code || transport.patient_type}${['302','303','304'].includes(transport.bh_code || transport.patient_type) ? ' Involuntary' : ' Voluntary'}`} />}
            {transport.round_trip_miles && <Badge bg="#f0f0f0" color="#666" label={`${transport.round_trip_miles} mi RT`} />}
            {transport.prepay_amount != null && (transport.prepay_status === 'paid'
              ? <Badge bg="#E1F5EE" color="#0F6E56" label={`PREPAID $${Number(transport.prepay_amount).toFixed(2)}`} />
              : <Badge bg="#C62828" color="white" label={`PREPAY DUE $${Number(transport.prepay_amount).toFixed(2)}`} />)}
          </div>
        </div>

        {/* Acuity context at the transporter-count decision point (Paula 2026-07-03):
            the system informs, the dispatcher decides. No scoring. */}
        {isBH && (
          <div style={{ margin: '12px 20px 0', background: '#FAECE7', border: '1px solid #E0A183', color: '#7A2E12', padding: '10px 12px', borderRadius: 8, fontSize: 13 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              BH {transport.bh_code || transport.patient_type} — {['302', '303', '304'].includes(transport.bh_code || transport.patient_type) ? 'INVOLUNTARY commitment' : 'voluntary'}
            </div>
            {transport.special_instructions ? (
              <div style={{ marginBottom: 4 }}><strong>Behavioral notes:</strong> {transport.special_instructions}</div>
            ) : (transport.notes || '').includes('Note:') ? (
              <div style={{ marginBottom: 4 }}><strong>Behavioral notes:</strong> {(transport.notes.match(/Note:\s*([^|]+)/) || [])[1] || 'see trip notes'}</div>
            ) : (
              <div style={{ marginBottom: 4, fontStyle: 'italic' }}>No behavioral notes on this trip.</div>
            )}
            <div style={{ fontSize: 12 }}>
              Sedan BH trips run 2 transporters when staffing allows. One transporter is your call — weigh the patient against the transporter. Choosing 1 will ask you to confirm.
            </div>
          </div>
        )}

        {/* Private-pay prepayment not collected yet */}
        {transport.prepay_amount != null && transport.prepay_status !== 'paid' && (
          <div style={{ margin: '12px 20px 0', background: '#FCEBEB', color: '#A32D2D', padding: '10px 12px', borderRadius: 8, fontSize: 13 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>💰 Prepayment not received</div>
            <div>This private-pay trip was quoted ${Number(transport.prepay_amount).toFixed(2)} for this leg. Payment (cash, check, or Zelle) is due before the trip runs. Mark it paid from the trip's Edit screen once collected.</div>
          </div>
        )}

        {/* Cannot fulfill warning */}
        {!canFulfill && !loading && (
          <div style={{ margin: '12px 20px 0', background: '#FCEBEB', color: '#A32D2D', padding: '10px 12px', borderRadius: 8, fontSize: 13 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>⚠️ Cannot auto-fulfill this trip</div>
            <div>{fulfillReason}</div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#A32D2D' }}>You can still manually select a vehicle and driver below, or reject this trip.</div>
          </div>
        )}

        {error && (
          <div style={{ margin: '12px 20px 0', background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '2rem', color: '#999', fontSize: 13, textAlign: 'center' }}>Loading...</div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto' }}>

            <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #f0f0f0' }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Vehicle</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {vehicles
                  .filter(v => !transport.requires_wheelchair || v.vehicle_type === 'wheelchair_van')
                  .map(v => {
                    const isBusy = busyVehicleIds.has(v.id);
                    const isSelected = selectedVehicle?.id === v.id;
                    return (
                      <div
                        key={v.id}
                        onClick={() => setSelectedVehicle(v)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 12px', borderRadius: 8,
                          cursor: 'pointer',
                          border: `0.5px solid ${isSelected ? '#185FA5' : isBusy ? '#FAC775' : '#e0e0db'}`,
                          background: isSelected ? '#E6F1FB' : isBusy ? '#FAEEDA22' : 'white',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: isSelected ? 500 : 400 }}>{v.name}</div>
                          <div style={{ fontSize: 11, color: isBusy ? '#BA7517' : '#aaa' }}>
                            {isBusy ? 'Busy at this time' : v.license_plate}
                          </div>
                        </div>
                        <Badge
                          bg={v.vehicle_type === 'sedan' ? '#E6F1FB' : '#EEEDFE'}
                          color={v.vehicle_type === 'sedan' ? '#0C447C' : '#3C3489'}
                          label={v.vehicle_type === 'sedan' ? 'Sedan' : 'WC'}
                        />
                        {isBusy && <Badge bg="#FAEEDA" color="#854F0B" label="Busy" />}
                        {isSelected && (
                          <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>

            <div style={{ padding: '14px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Drivers</div>
                <div style={{ fontSize: 11, color: '#999' }}>
                  {selectedDrivers.length} selected — tap to add/remove
                </div>
              </div>

              {selectedDrivers.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {selectedDrivers.map((d, i) => (
                    <DriverPill key={d.id} driver={d} index={i} onRemove={() => toggleDriver(d)} />
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {drivers.map(d => {
                  const isSelected = !!selectedDrivers.find(s => s.id === d.id);
                  const isUnavailable = !d.isAvailable;
                  const isBusy = busyDriverIds.has(d.id);
                  const cantDo = (transport.requires_wheelchair && !d.capabilities?.wheelchair) ||
                    (isBH && !d.capabilities?.bh201);
                  const disabled = isUnavailable || isBusy || cantDo;

                  return (
                    <div
                      key={d.id}
                      onClick={() => !cantDo && toggleDriver(d)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px', borderRadius: 8,
                        border: `0.5px solid ${isSelected ? '#185FA5' : cantDo ? '#f0f0f0' : isBusy ? '#FAC775' : '#e0e0db'}`,
                        background: isSelected ? '#E6F1FB' : cantDo ? '#fafafa' : 'white',
                        cursor: cantDo ? 'not-allowed' : 'pointer',
                        opacity: cantDo ? 0.5 : 1,
                      }}
                    >
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: isSelected ? '#185FA5' : '#f0f0f0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 500,
                        color: isSelected ? 'white' : '#666',
                      }}>
                        {d.firstName?.[0]}{d.lastName?.[0]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: isSelected ? 500 : 400 }}>{d.name}</div>
                        <div style={{ fontSize: 10, color: '#999', marginTop: 1 }}>
                          {(Math.ceil((d.weeklyStats?.hoursThisWeek || 0) * 4) / 4).toFixed(2)}h this week
                          {isBusy ? ' · busy at this time' : ''}
                          {isUnavailable && !isBusy ? ' · unavailable today' : ''}
                          {cantDo ? ' · not qualified' : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {(() => {
                          const dayHrs = Math.ceil((d.hos?.todayMinutes || 0) / 60 * 4) / 4;
                          const weekHrs = Math.ceil((d.hos?.sevenDayMinutes || 0) / 60 * 4) / 4;
                          if (dayHrs >= 9.5)  return <Badge bg="#FCEBEB" color="#A32D2D" label={`🛑 ${dayHrs.toFixed(2)}/10 today`} />;
                          if (dayHrs >= 8)    return <Badge bg="#FAEEDA" color="#854F0B" label={`⚠️ ${dayHrs.toFixed(2)}/10 today`} />;
                          if (weekHrs >= 58)  return <Badge bg="#FCEBEB" color="#A32D2D" label={`🛑 ${weekHrs.toFixed(2)}/60 wk`} />;
                          if (weekHrs >= 54)  return <Badge bg="#FAEEDA" color="#854F0B" label={`⚠️ ${weekHrs.toFixed(2)}/60 wk`} />;
                          return null;
                        })()}
                        {d.hos?.isDrivingNow && <Badge bg="#E1F5EE" color="#0F6E56" label="⏱️ driving" />}
                        {d.lineClosed && <Badge bg="#FCEBEB" color="#A32D2D" label="📵 no texts" />}
                        {d.isFullTime && <Badge bg="#E6F1FB" color="#185FA5" label="FT" />}
                        {d.availabilityNote === 'available_extra' && <Badge bg="#FAEEDA" color="#854F0B" label="On-call" />}
                        {d.capabilities?.bh302 && !d.capabilities?.avoidAggressive302 && <Badge bg="#E1F5EE" color="#0F6E56" label="All BH" />}
                        {isBusy && <Badge bg="#FAC775" color="#854F0B" label="Busy" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {showTransporterWarning && (
          <div style={{ margin: '0 20px 0', padding: '8px 12px', background: '#FAEEDA', border: '0.5px solid #FAC775', borderRadius: 8, fontSize: 12, color: '#854F0B' }}>
            This is an LRH / Willow Valley trip — typically dispatched with 1 transporter only. You have {selectedDrivers.length} selected.
          </div>
        )}
        {showTwoAssistWarning && (
          <div style={{ margin: '0 20px 8px', padding: '8px 12px', background: '#FCEBEB', border: '0.5px solid #E0A6A6', borderRadius: 8, fontSize: 12, color: '#A32D2D', fontWeight: 600 }}>
            ⚠ This patient is flagged 2-PERSON ASSIST on their profile — one driver is not enough for this pickup.
          </div>
        )}
        {selectedDrivers.some(d => d.lineClosed) && (
          <div style={{ margin: '0 20px 8px', padding: '8px 12px', background: '#FCEBEB', border: '0.5px solid #E0A6A6', borderRadius: 8, fontSize: 12, color: '#A32D2D' }}>
            📵 {selectedDrivers.filter(d => d.lineClosed).map(d => d.firstName || d.name).join(', ')} won't get a Rosie text — their message line is closed. Call them, or have them text Rosie first.
          </div>
        )}
        {/* Visible flag: 1 transporter on a sedan (non-BH) — allowed, no friction, just seen */}
        {selectedDrivers.length === 1 && !transport.requires_wheelchair && String(selectedVehicle?.vehicle_type || '').toLowerCase().includes('sedan') && !isBH && (
          <div style={{ margin: '0 20px 8px', padding: '6px 10px', background: '#FAEEDA', color: '#854F0B', borderRadius: 6, fontSize: 12 }}>
            1 transporter on a sedan — this facility's sedans usually run 2 when staffing allows.
          </div>
        )}
        {selectedDrivers.length === 1 && String(selectedVehicle?.vehicle_type || '').toLowerCase().includes('sedan') && isBH && (
          <div style={{ margin: '0 20px 8px', padding: '6px 10px', background: '#FAECE7', color: '#993C1D', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
            1 transporter on a BH sedan — dispatch will ask you to confirm and your confirmation is logged.
          </div>
        )}
        <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e0e0db', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ fontSize: 13 }}>Cancel</button>
          {hasConflict && (
            <button
              onClick={() => handleDispatch(true)}
              disabled={saving || loading || !selectedVehicle || selectedDrivers.length === 0}
              style={{ fontSize: 13, background: '#BA7517', color: 'white', border: '0.5px solid #BA7517', padding: '6px 16px' }}
            >
              {saving ? 'Dispatching...' : 'Dispatch anyway'}
            </button>
          )}
          <button
            onClick={() => handleDispatch(false)}
            disabled={saving || loading || !selectedVehicle || selectedDrivers.length === 0}
            style={{ fontSize: 13, background: '#1D9E75', color: 'white', border: '0.5px solid #1D9E75', padding: '6px 16px' }}
          >
            {saving ? 'Saving...' : ['en_route_pickup', 'picked_up', 'dropped_off', 'dispatched'].includes(transport.status) ? 'Reassign & dispatch' : 'Approve & dispatch'}
          </button>
        </div>
      </div>
    </div>
  );
}